import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import { useAdmin } from "../../hooks/useAdmin";
import { formatUsd } from "../../utils/currency";
import { formatRelativeTime } from "../../utils/time";
import { Alert } from "../common/Alert";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { EmptyState } from "../common/EmptyState";
import { FormCheckbox } from "../common/forms/FormCheckbox";
import { FormInput } from "../common/forms/FormInput";
import { FormSelect } from "../common/forms/FormSelect";
import { LoadingState } from "../common/LoadingState";
import { MetricCard } from "../common/MetricCard";
import { PanelHeaderBlock } from "../common/PanelHeaderBlock";
import { PanelSection } from "../common/PanelSection";
import { StatusBadge } from "../common/StatusBadge";
import { Text } from "../common/Text";
import { AdminActiveFiltersRow } from "./AdminActiveFiltersRow";
import { AdminOrganizationWorkspace } from "./AdminOrganizationWorkspace";

const STATUS_FILTER_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
  { label: "Inactive", value: "inactive" },
];

const LOW_BALANCE_THRESHOLD_USD = 5;

type AdminOrganization = ReturnType<typeof useAdmin>["organizations"][number];

const AdminOrganizationRow: FC<{
  isSelected: boolean;
  onSelect: (organizationId: string) => void;
  organization: AdminOrganization;
}> = ({ isSelected, onSelect, organization }) => (
  <DataTable.Row
    className={`cursor-pointer ${isSelected ? "bg-emerald-50/60 dark:bg-emerald-500/10" : ""}`}
    onClick={() => onSelect(organization.organization_id)}
  >
    <DataTable.Cell className="font-mono text-[11px] font-bold text-gray-900 dark:text-white">
      {organization.name}
    </DataTable.Cell>
    <DataTable.Cell className="font-mono text-xs text-gray-500 dark:text-gray-400">
      {organization.slug}
    </DataTable.Cell>
    <DataTable.Cell>
      <StatusBadge status={organization.plan_tier} size="sm" />
    </DataTable.Cell>
    <DataTable.Cell className="text-xs text-gray-700 dark:text-gray-300">
      {organization.status}
    </DataTable.Cell>
    <DataTable.Cell className="font-mono text-xs text-gray-700 dark:text-gray-300">
      {formatUsd(organization.credit_balance_usd)}
    </DataTable.Cell>
    <DataTable.Cell className="text-xs text-gray-700 dark:text-gray-300">
      {organization.member_count}
    </DataTable.Cell>
    <DataTable.Cell className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
      {formatRelativeTime(new Date(organization.created_at).getTime())}
    </DataTable.Cell>
    <DataTable.Cell className="font-mono text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-300">
      {isSelected ? "Open" : "Manage"}
    </DataTable.Cell>
  </DataTable.Row>
);

const SelectedOrganizationSummary: FC<{
  organization: AdminOrganization | null;
}> = ({ organization }) => {
  if (!organization) {
    return (
      <div className="rounded-none border border-slate-300 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-arti-background">
        <div className="flex items-center justify-between gap-3">
          <Text variant="label">Focused Workspace</Text>
          <Badge variant="default" size="sm">
            none selected
          </Badge>
        </div>
        <Text as="p" className="mt-3 leading-5">
          Pick an organization from the directory to expose its credits and API
          key workspace here.
        </Text>
      </div>
    );
  }

  const lowBalance =
    organization.credit_balance_usd <= LOW_BALANCE_THRESHOLD_USD;

  return (
    <div className="rounded-none border border-slate-300 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-arti-background">
      <div className="flex items-center justify-between gap-3">
        <Text variant="label">Focused Workspace</Text>
        <StatusBadge status={organization.status} size="sm" />
      </div>

      <div className="mt-3 space-y-3">
        <div>
          <p className="font-mono text-sm font-bold text-slate-900 dark:text-white">
            {organization.name}
          </p>
          <Text as="p" className="mt-1 leading-5">
            {organization.slug} · {organization.member_count} members ·{" "}
            {formatUsd(organization.credit_balance_usd)} available
          </Text>
        </div>

        <div className="flex flex-wrap gap-2">
          <Badge size="sm" variant={lowBalance ? "warning" : "success"}>
            {lowBalance ? "low balance" : "healthy balance"}
          </Badge>
          <Badge variant="status" size="sm">
            {organization.plan_tier}
          </Badge>
        </div>
      </div>
    </div>
  );
};

const OrganizationsDirectoryTable: FC<{
  organizations: readonly AdminOrganization[];
  selectedOrgId: string | null;
  onSelect: (organizationId: string) => void;
}> = ({ organizations, selectedOrgId, onSelect }) => (
  <DataTable responsive>
    <DataTable.Header>
      <tr>
        <DataTable.HeaderCell>Name</DataTable.HeaderCell>
        <DataTable.HeaderCell>Slug</DataTable.HeaderCell>
        <DataTable.HeaderCell>Plan</DataTable.HeaderCell>
        <DataTable.HeaderCell>Status</DataTable.HeaderCell>
        <DataTable.HeaderCell>Credits</DataTable.HeaderCell>
        <DataTable.HeaderCell>Members</DataTable.HeaderCell>
        <DataTable.HeaderCell>Created</DataTable.HeaderCell>
        <DataTable.HeaderCell>Workspace</DataTable.HeaderCell>
      </tr>
    </DataTable.Header>
    <DataTable.Body>
      {organizations.length === 0 ? (
        <DataTable.Row>
          <DataTable.Cell
            colSpan={8}
            className="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
          >
            No organizations match the current filters.
          </DataTable.Cell>
        </DataTable.Row>
      ) : (
        organizations.map((org) => (
          <AdminOrganizationRow
            key={org.organization_id}
            isSelected={org.organization_id === selectedOrgId}
            onSelect={onSelect}
            organization={org}
          />
        ))
      )}
    </DataTable.Body>
  </DataTable>
);

export const AdminOrganizationsTab: FC = () => {
  const { organizations, loading, error, refresh } = useAdmin();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [lowBalanceOnly, setLowBalanceOnly] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const normalizedQuery = useMemo(() => query.trim().toLowerCase(), [query]);

  const planFilterOptions = useMemo(() => {
    const plans = new Set<string>();
    for (const org of organizations) {
      plans.add(org.plan_tier);
    }
    return [
      { label: "All Plans", value: "" },
      ...Array.from(plans)
        .toSorted((a, b) => a.localeCompare(b))
        .map((plan) => ({ label: plan, value: plan })),
    ];
  }, [organizations]);

  const filteredOrganizations = useMemo(
    () =>
      organizations
        .filter((org) => {
          if (
            normalizedQuery &&
            !org.name.toLowerCase().includes(normalizedQuery) &&
            !org.slug.toLowerCase().includes(normalizedQuery)
          ) {
            return false;
          }
          if (statusFilter && org.status !== statusFilter) {
            return false;
          }
          if (planFilter && org.plan_tier !== planFilter) {
            return false;
          }
          if (
            lowBalanceOnly &&
            org.credit_balance_usd > LOW_BALANCE_THRESHOLD_USD
          ) {
            return false;
          }
          return true;
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [organizations, normalizedQuery, statusFilter, planFilter, lowBalanceOnly],
  );

  useEffect(() => {
    if (filteredOrganizations.length === 0) {
      setSelectedOrgId(null);
      return;
    }
    if (
      !selectedOrgId ||
      !filteredOrganizations.some(
        (org) => org.organization_id === selectedOrgId,
      )
    ) {
      setSelectedOrgId(filteredOrganizations[0]?.organization_id ?? null);
    }
  }, [filteredOrganizations, selectedOrgId]);

  const selectedOrg = useMemo(
    () =>
      filteredOrganizations.find(
        (org) => org.organization_id === selectedOrgId,
      ) ?? null,
    [filteredOrganizations, selectedOrgId],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  const resetFilters = useCallback(() => {
    setLowBalanceOnly(false);
    setPlanFilter("");
    setQuery("");
    setStatusFilter("");
  }, []);

  const summary = useMemo(() => {
    const activeCount = organizations.filter(
      (org) => org.status === "active",
    ).length;
    const lowBalanceCount = organizations.filter(
      (org) => org.credit_balance_usd <= LOW_BALANCE_THRESHOLD_USD,
    ).length;
    const totalCreditsUsd = organizations.reduce(
      (total, org) => total + org.credit_balance_usd,
      0,
    );
    return { activeCount, lowBalanceCount, totalCreditsUsd };
  }, [organizations]);

  const activeFilters = useMemo(() => {
    const filters = [];
    if (normalizedQuery) {
      filters.push({
        id: "query",
        label: `Search: ${query.trim()}`,
        onClear: () => setQuery(""),
      });
    }
    if (statusFilter) {
      filters.push({
        id: "status",
        label: `Status: ${statusFilter}`,
        onClear: () => setStatusFilter(""),
      });
    }
    if (planFilter) {
      filters.push({
        id: "plan",
        label: `Plan: ${planFilter}`,
        onClear: () => setPlanFilter(""),
      });
    }
    if (lowBalanceOnly) {
      filters.push({
        id: "low-balance",
        label: `Credits <= ${formatUsd(LOW_BALANCE_THRESHOLD_USD)}`,
        onClear: () => setLowBalanceOnly(false),
      });
    }
    return filters;
  }, [lowBalanceOnly, normalizedQuery, planFilter, query, statusFilter]);

  const metricCards = [
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
      label: "Organizations",
      subValue: `${filteredOrganizations.length} visible now`,
      value: organizations.length,
      variant: "indigo",
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
      label: "Active",
      subValue: `${organizations.length - summary.activeCount} not active`,
      value: summary.activeCount,
      variant: "emerald",
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
      label: `Low Balance (<= ${formatUsd(LOW_BALANCE_THRESHOLD_USD)})`,
      subValue: lowBalanceOnly ? "priority queue enabled" : "needs review",
      value: summary.lowBalanceCount,
      variant: "amber",
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      label: "Total Credits",
      subValue: selectedOrg
        ? `${selectedOrg.name} selected`
        : "platform-wide balance",
      value: formatUsd(summary.totalCreditsUsd),
      variant: "violet",
    },
  ] as const;

  if (loading) {
    return <LoadingState message="Loading organizations…" />;
  }
  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  return (
    <div className="space-y-4">
      <PanelSection
        badge={
          <Badge variant="info" size="sm">
            platform admin
          </Badge>
        }
        headerContent={
          <PanelHeaderBlock
            eyebrow="Operations"
            title="Organization Command Center"
            description="Scan account health, narrow the directory, and open a workspace without leaving the admin surface."
          />
        }
      >
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.9fr)]">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {metricCards.map((card) => (
              <MetricCard
                key={card.label}
                label={card.label}
                value={card.value}
                variant={card.variant}
                size="lg"
                subValue={card.subValue}
                icon={card.icon}
              />
            ))}
          </div>

          <SelectedOrganizationSummary organization={selectedOrg} />
        </div>
      </PanelSection>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(340px,0.95fr)]">
        <PanelSection
          badge={
            <Badge variant="default" size="sm">
              {filteredOrganizations.length} shown
            </Badge>
          }
          headerContent={
            <PanelHeaderBlock
              eyebrow="Directory"
              title="Organizations"
              description="Filter by lifecycle, plan tier, or balance risk to find the account you need."
            />
          }
        >
          <div className="mb-3 grid gap-3 xl:grid-cols-[minmax(220px,1fr)_180px_180px_auto_auto]">
            <FormInput
              label="Search"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Search by name or slug"
              size="sm"
            />
            <FormSelect
              label="Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.currentTarget.value)}
              options={STATUS_FILTER_OPTIONS}
              size="sm"
            />
            <FormSelect
              label="Plan"
              value={planFilter}
              onChange={(event) => setPlanFilter(event.currentTarget.value)}
              options={planFilterOptions}
              size="sm"
            />
            <div className="mt-5">
              <FormCheckbox
                label="Low balance only"
                checked={lowBalanceOnly}
                onChange={setLowBalanceOnly}
              />
            </div>
            <div className="mt-4 flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleRefresh()}
                loading={refreshing}
                loadingText="Refreshing..."
              >
                Refresh
              </Button>
            </div>
          </div>

          <AdminActiveFiltersRow
            filters={activeFilters}
            onReset={resetFilters}
            totalLabel={`${filteredOrganizations.length} of ${organizations.length} visible`}
          />

          <OrganizationsDirectoryTable
            organizations={filteredOrganizations}
            selectedOrgId={selectedOrgId}
            onSelect={setSelectedOrgId}
          />
        </PanelSection>

        {selectedOrg ? (
          <AdminOrganizationWorkspace
            organization={selectedOrg}
            onRefresh={refresh}
          />
        ) : (
          <PanelSection
            headerContent={
              <PanelHeaderBlock
                eyebrow="Workspace"
                title="Organization Workspace"
                description="Open an organization to manage credits and provider assignments."
              />
            }
          >
            <EmptyState
              title="No organization selected"
              description="Choose an organization from the directory to manage credits and API keys."
            />
          </PanelSection>
        )}
      </div>
    </div>
  );
};
